import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2Icon, Loader2Icon } from 'lucide-react';
const questions = [{
  id: 1,
  question: 'In welke situatie lijkt jouw hond het vaakst jou te negeren?',
  options: ['Buiten tijdens het wandelen, als er veel afleiding is.', 'Als er visite binnenkomt, de deurbel gaat of hij iets hoort van buiten.', 'Als hij andere honden ziet of passeert.', 'Thuis, zonder duidelijke aanleiding of op willekeurige momenten.']
}, {
  id: 2,
  question: "Als jouw hond in zo'n situatie niet luistert, wat doet hij dan wel?",
  options: ['Hij is volledig gefocust op de prikkel (bijv. de andere hond) en is dan onbereikbaar.', 'Hij schiet volledig in zijn emotie (angst, agressie, of iets anders).', 'Hij begint te blaffen, piepen of valt uit.', 'Hij deinst terug, probeert weg te komen of verstopt zich.']
}, {
  id: 3,
  question: 'Wat dacht je tot nu toe dat de reden was van dit gedrag?',
  options: ['Dat hij dominant probeert te zijn en de leiding wil nemen.', 'Dat dit verkeerd aangeleerd gedrag is.', 'Dat hij mij negeert omdat ik niet streng genoeg ben.', 'Ik dacht altijd dat hij gewoon ongehoorzaam of koppig was.']
}, {
  id: 4,
  question: 'Wat heb je tot nu toe geprobeerd om hem ander gedrag aan te leren?',
  options: ['Hem fysiek corrigeren (bijv. aan de lijn trekken, wegduwen, corrigeren, straffen, etc).', 'Mijn stem verheffen, "Nee!" roepen of streng toespreken.', 'Hem proberen af te leiden met snoepjes.', 'Geduldig de situatie met hem oefenen en hem met lichaamstaal vertellen wat ik wel en niet goed vindt (wat niet helemaal lukt).']
}, {
  id: 5,
  question: 'Zijn er dingen die dit gedrag erger maken?',
  options: ['Als ik zie dat hij hoog in zijn stress zit is het erger.', 'Op sommige dagen is het minder maar ik weet niet precies hoezo.', 'Misschien is zijn "emmertje" overgelopen en kán hij simpelweg niet meer luisteren.', 'Ja, ik weet precies hoe ik dit kan voorkomen of verminderen. Het lukt me alleen niet altijd.']
}, {
  id: 6,
  question: 'Wat is voor jou het belangrijkste doel in de relatie met je hond?',
  options: ["Ik wil dat hij commando's opvolgt zodat ik controle heb.", 'Ik wil dat we elkaar écht begrijpen en in harmonie samenleven.', 'Ik wil dat hij zich ontspannen en veilig voelt, ook buiten of bij visite.', 'Ik wil een diepe vertrouwensband opbouwen zonder dwang of straf.']
}];
export function QuizPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const navigate = useNavigate();
  const handleAnswer = (optionIndex: number) => {
    const newAnswers = [...answers, optionIndex];
    setAnswers(newAnswers);
    setTimeout(() => {
      if (currentStep < questions.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        setIsAnalyzing(true);
      }
    }, 300);
  };
  useEffect(() => {
    if (isAnalyzing) {
      const timer = setTimeout(() => {
        setIsAnalyzing(false);
        setShowResults(true);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [isAnalyzing]);
  const progress = (currentStep + 1) / questions.length * 100;
  return <div className="min-h-screen bg-white font-sans text-gray-900 flex flex-col" data-id="element-1376">
      {/* Header with Logo */}
      <header className="w-full py-6 px-4 flex justify-center border-b border-gray-100" data-id="element-1377">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6YKzftRpGkz4jKTtn8FQxB/Logo_Stressless_Dogs_%288%29_%284%29.png" alt="Stressless Dogs Logo" className="h-12 object-contain" data-id="element-1378" />
      </header>

      <main className="flex-1 flex flex-col items-center px-4 py-8 md:py-12 w-full max-w-3xl mx-auto" data-id="element-1379">
        {!isAnalyzing && !showResults && <div className="w-full mb-8" data-id="element-1380">
            {/* Progress Bar */}
            <div className="flex justify-between items-center mb-2" data-id="element-1381">
              <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1382">
                Vraag {currentStep + 1} van {questions.length}
              </span>
              <span className="text-sm font-bold text-green-600" data-id="element-1383">
                {Math.round(progress)}%
              </span>
            </div>
            <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-1384">
              <motion.div className="h-full bg-green-500 rounded-full" initial={{
            width: 0
          }} animate={{
            width: `${progress}%`
          }} transition={{
            duration: 0.5,
            ease: 'easeInOut'
          }} data-id="element-1385" />
            </div>
          </div>}

        <div className="w-full relative min-h-[400px]" data-id="element-1386">
          <AnimatePresence mode="wait" data-id="element-1387">
            {isAnalyzing ? <motion.div key="analyzing" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} className="absolute inset-0 flex flex-col items-center justify-center text-center" data-id="element-1388">
                <Loader2Icon className="w-16 h-16 text-green-500 animate-spin mb-6" data-id="element-1389" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1390">
                  Antwoorden analyseren...
                </h2>
                <p className="text-gray-600" data-id="element-1391">
                  We berekenen jouw persoonlijke resultaat op basis van je
                  antwoorden.
                </p>
              </motion.div> : showResults ? <motion.div key="results" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="w-full bg-blue-50 border border-blue-100 rounded-2xl p-6 md:p-10 shadow-sm" data-id="element-1392">
                <div className="flex items-center justify-center mb-6" data-id="element-1393">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center" data-id="element-1394">
                    <CheckCircle2Icon className="w-8 h-8 text-green-600" data-id="element-1395" />
                  </div>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-6" data-id="element-1396">
                  Bedankt voor je antwoorden.
                </h1>

                <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm mb-8" data-id="element-1397">
                  <p className="text-lg text-gray-800 leading-relaxed mb-4" data-id="element-1398">
                    <span className="font-bold text-gray-900" data-id="element-1399">
                      Wat direct opvalt:
                    </span>{' '}
                    de echte reden dat je hond niet luistert, heeft niets te
                    maken met dominantie of ongehoorzaamheid. Het is een
                    klassiek geval van miscommunicatie.
                  </p>
                  <p className="text-lg text-gray-800 leading-relaxed mb-4" data-id="element-1400">
                    Jij probeert je hond te vertellen dat bepaald gedrag niet
                    nodig is, maar je hond snapt dit niet omdat er bepaalde
                    communicatie signalen missen die een hond nodig heeft.
                  </p>
                  <p className="text-lg text-gray-800 leading-relaxed" data-id="element-1401">
                    Andersom geeft hij waarschijnlijk subtiele signalen af die
                    verklaren waar dit gedrag vandaan komt. Als je daarop
                    inspeelt kan je vaak voorkomen dat dit gedrag opspeelt.
                  </p>
                </div>

                <div className="text-center" data-id="element-1402">
                  <p className="text-lg font-semibold text-gray-900 mb-6" data-id="element-1403">
                    In het gratis webinar van gedragsexpert Amee Buijs kan je
                    precies zien hoe natuurlijke hondentaal werkt, zodat jullie
                    elkaar écht gaan begrijpen.
                  </p>
                  <button onClick={() => navigate('/webinar-mc')} className="w-full md:w-auto bg-green-500 hover:bg-green-600 text-white font-bold py-5 px-10 rounded-xl transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-md text-xl mb-8" data-id="element-1404">
                    Bekijk wanneer het volgende gratis webinar is
                  </button>
                  <div className="flex flex-col items-center" data-id="element-1405">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kJ5b6tBbH8hDNt52JG84mT/Scherm%C2%ADafbeelding_2025-08-15_om_15.44.02.png" alt="Amee de Bruijn" className="w-20 h-20 rounded-full object-cover shadow-sm" data-id="element-1406" />
                    <p className="text-sm text-gray-600 mt-2 font-medium" data-id="element-1407">
                      Amee de Bruijn
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1408">
                      Gediplomeerd Hondengedragsconsultant
                    </p>
                  </div>
                </div>
              </motion.div> : <motion.div key={currentStep} initial={{
            opacity: 0,
            x: 50
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -50
          }} transition={{
            duration: 0.3
          }} className="w-full" data-id="element-1409">
                {currentStep === 0 && <h1 className="text-2xl md:text-3xl font-bold text-center text-blue-900 mb-8 leading-tight" data-id="element-1410">
                    Waarom luistert jouw hond niet? Doe de quiz en ontdek de
                    echte oorzaak
                  </h1>}

                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-8" data-id="element-1411">
                  {questions[currentStep].question}
                </h2>

                <div className="space-y-4" data-id="element-1412">
                  {questions[currentStep].options.map((option, index) => <button key={index} onClick={() => handleAnswer(index)} className="w-full text-left p-5 md:p-6 rounded-xl border-2 border-gray-100 bg-white hover:border-green-500 hover:bg-green-50 transition-all duration-200 shadow-sm hover:shadow-md group flex items-start" data-id="element-1413">
                      <div className="w-8 h-8 rounded-full border-2 border-gray-300 group-hover:border-green-500 flex items-center justify-center mr-4 flex-shrink-0 mt-0.5" data-id="element-1414">
                        <span className="text-gray-500 group-hover:text-green-600 font-semibold" data-id="element-1415">
                          {String.fromCharCode(65 + index)}
                        </span>
                      </div>
                      <span className="text-lg text-gray-700 group-hover:text-gray-900 font-medium leading-relaxed" data-id="element-1416">
                        {option}
                      </span>
                    </button>)}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </main>

      {/* Sticky mobile CTA - only visible on results page */}
      {showResults && <div className="fixed bottom-0 left-0 right-0 z-40 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.1)] md:hidden" data-id="element-1417">
          <div className="px-4 py-3 text-center" data-id="element-1418">
            <button onClick={() => navigate('/webinar-mc')} className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg transition-colors" data-id="element-1419">
              <span className="text-base block" data-id="element-1420">
                Bekijk wanneer het volgende gratis webinar is
              </span>
              <span className="text-xs font-normal opacity-90 block mt-0.5" data-id="element-1421">
                zodat je dit kan oplossen
              </span>
            </button>
          </div>
        </div>}
    </div>;
}